import React from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Receipt, Download, ExternalLink } from "lucide-react";
import { format, fromUnixTime } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";

export function InvoicesTab() {
  const invoiceData = useQuery(api.subscriptions.getInvoiceHistory, { limit: 20 });

  if (invoiceData === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Paid</Badge>;
      case "open":
        return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">Open</Badge>;
      case "void":
        return <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">Void</Badge>;
      case "uncollectible":
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Uncollectible</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: string, currency: string) => {
    const numAmount = parseFloat(amount) / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(numAmount);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Receipt className="h-5 w-5" />
            Invoice History
          </CardTitle>
          <CardDescription>View and download your past invoices</CardDescription>
        </CardHeader>
        <CardContent>
          {invoiceData.invoices.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No invoices yet"
              description="Your invoices will appear here once you have an active subscription."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Invoice Number</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoiceData.invoices.map((invoice: any) => (
                    <TableRow key={invoice._id}>
                      <TableCell className="font-medium">
                        {invoice.createdTime
                          ? format(fromUnixTime(invoice.createdTime), "MMM d, yyyy")
                          : "N/A"}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-gray-500">
                        {invoice.invoiceNumber ||
                          invoice.invoiceId.substring(0, 20) + (invoice.invoiceNumber ? "" : "...")}
                      </TableCell>
                      <TableCell>{formatCurrency(invoice.amountPaid, invoice.currency)}</TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              if (invoice.invoicePdfUrl) {
                                window.open(invoice.invoicePdfUrl, "_blank");
                              } else {
                                // Fallback to Stripe invoice URL format
                                window.open(
                                  `https://dashboard.stripe.com/invoices/${invoice.invoiceId}`,
                                  "_blank"
                                );
                              }
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      {invoiceData.invoices.length > 0 && (
        <Card className="bg-gray-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total invoices</p>
                <p className="text-2xl font-bold text-gray-900">{invoiceData.invoices.length}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Total paid</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(
                    invoiceData.invoices
                      .filter((inv: any) => inv.status === "paid")
                      .reduce((sum: number, inv: any) => sum + parseFloat(inv.amountPaid), 0)
                      .toString(),
                    invoiceData.invoices[0]?.currency || "usd"
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
